// The Mail workspace's Chases due tab (Session AUH, email system Phase 7, item 5). Every thing ELE is waiting on from someone
// else (an answer to a quote, a decision, information for a booked visit, a document, a delivery, a payment) is ONE chase,
// found by the server's detectors (server/src/modules/chases/detectors.ts) or opened here by hand.
//
// THE DAILY RUN ONLY DRAFTS (06:15 UTC). Each due chase's draft sits in Gmail and here; NOTHING GOES UNTIL A PERSON PRESSES
// SEND. The batch is: tick the drafts, "Send selected", and the branded confirm (the BRANDED CONFIRM rule) asks the server's
// preview first, which re-checks every chase (a reply, a payment, a stop condition, a changed draft) against the fingerprint
// this page showed and sends nothing, and names exactly who each goes to (To, Cc) and From which address. Only the confirm's
// own Send button sends, and only the chases the preview said would go. Every result is shown, a failure never hiding the rest.
//
// Each row says WHY it is due and WHY it is soft or hard, in the server's words (timeline.ts). The office can change the tone
// or the due date (the daily run never overrides it), snooze, stop with a reason (the platform's own Gmail draft is removed),
// turn it into a phone call, and for a debt prepare and send the referral pair, which go together or not at all.
import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import type { Chase, ChaseFailure, ChaseListResponse, ChaseSendPreviewItem, ChaseSendResult, ChaseTone } from "../../../lib/api";
import { friendlyApiError } from "../../../lib/friendlyApiError";
import { fmtLondonDateTime } from "../../../lib/mailWorkspace";
import { ConfirmButton } from "../../../components/ConfirmButton";
import { EmptyState } from "../../../components/EmptyState";
import { Loader } from "../../../components/Loader";
import { StatusPill } from "../../../components/StatusPill";
import { formatDateLong, londonDateKey } from "../../../lib/format";
import { ModalShell, MODAL_ACTIONS, MODAL_PRIMARY_BTN, MODAL_SECONDARY_BTN } from "../../../components/ModalShell";
import { Button } from "../../../components/Button";

const SMALL_BTN =
  "rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:bg-ele-inset hover:text-ele-strong disabled:opacity-50 max-sm:min-h-11";
const AMBER_NOTE = "rounded-ele border border-amber-500 bg-amber-500/10 px-3 py-2 text-sm text-[var(--ele-amber-text-soft)]";
const DANGER_NOTE = "rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong";
const INPUT = "min-h-11 rounded-ele border border-ele-purple-accent bg-ele-inset px-3 py-2 text-sm text-ele-strong sm:min-h-0";

export const CHASE_VIEWS = ["due", "calls", "referral", "all", "ended"] as const;
export type ChaseView = (typeof CHASE_VIEWS)[number];
const VIEW_LABELS: Record<ChaseView, string> = {
  due: "Due now",
  calls: "Phone calls",
  referral: "Ready to refer",
  all: "All open",
  ended: "Ended",
};

/** A London YYYY-MM-DD, for a date input ("" when there is no date). */
function isoDay(iso: string | null | undefined): string {
  const key = londonDateKey(iso);
  return key === "-" ? "" : key;
}

/** Can this chase be ticked for sending? Only a draft that passed its checks and is the email (not the referral pair). */
export function canSelectChase(c: Chase): boolean {
  return c.status === "open" && c.mode === "email" && !!c.draft?.sendable && !!c.draft.fingerprint;
}

function pct(n: number | null): string {
  return n === null ? "Not measured" : `${Math.round(n * 100)}%`;
}

function chaseLabel(c: Pick<Chase, "title" | "reference">): string {
  return c.reference && !c.title.includes(c.reference) ? `${c.title} (${c.reference})` : c.title;
}

export function ChasesTab() {
  const [view, setView] = useState<ChaseView>("due");
  const [search, setSearch] = useState("");
  const [data, setData] = useState<ChaseListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [confirmFor, setConfirmFor] = useState<Chase[] | null>(null);
  const [referFor, setReferFor] = useState<Chase | null>(null);
  const [results, setResults] = useState<Array<{ result: ChaseSendResult; label: string }> | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [running, setRunning] = useState(false);
  const [runNote, setRunNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params =
        view === "ended" ? { status: "ended" as const } : view === "all" ? { status: "open" as const } : { status: "open" as const, view: view as "due" | "calls" | "referral" };
      const res = await api.chases.list({ ...params, ...(search.trim() ? { search: search.trim() } : {}) });
      setData(res);
      setSelected((prev) => {
        const ok = new Set(res.items.filter(canSelectChase).map((c) => c.id));
        return new Set([...prev].filter((k) => ok.has(k)));
      });
    } catch (err) {
      setError(friendlyApiError(err, "Could not load the chases."));
    } finally {
      setLoading(false);
    }
  }, [view, search]);

  useEffect(() => {
    void load();
    // Search is applied on submit, not per keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const items = data?.items ?? null;
  const selectable = useMemo(() => (items ?? []).filter(canSelectChase), [items]);
  const chosen = useMemo(() => selectable.filter((c) => selected.has(c.id)), [selectable, selected]);
  const allTicked = selectable.length > 0 && chosen.length === selectable.length;

  function replaceRow(next: Chase | null | undefined) {
    if (!next) return;
    setData((d) => (d ? { ...d, items: d.items.map((c) => (c.id === next.id ? next : c)) } : d));
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function runNow() {
    setRunning(true);
    setRunNote(null);
    try {
      await api.chases.run();
      setRunNote("Every source was checked and the due chases were drafted. Nothing was sent.");
      await load();
    } catch (err) {
      setRunNote(friendlyApiError(err, "The chase run could not be started."));
    } finally {
      setRunning(false);
    }
  }

  const m = data?.metrics;
  const header = (
    <div className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3 text-sm text-ele-body" data-testid="chases-header">
      <p className="text-ele-strong">Everything we are waiting on from someone else, chased on a ladder.</p>
      <p className="mt-1">
        Each morning the platform drafts the chases that are due, softly at first and firmer when the reason is given below. Nothing
        goes until someone presses Send. A reply, a payment or the record moving on stops a chase before its next draft.
      </p>
      {m && (
        <p className="mt-2 text-xs" data-testid="chases-metrics">
          <span className="text-ele-strong">{m.open}</span> open · <span className="text-ele-strong">{m.dueNow}</span> due now ·{" "}
          <span className="text-ele-strong">{m.calls}</span> to phone · last {m.windowDays} days: {m.totals.sent} sent, replies within 5 working days{" "}
          <span className="text-ele-strong">{pct(m.totals.replyRate)}</span>, {m.totals.resolved} resolved
        </p>
      )}
      {data && (
        <p className="mt-1 text-xs" data-testid="chases-last-run">
          {data.lastRunAt ? `Last checked ${fmtLondonDateTime(data.lastRunAt)}.` : "The daily chase run has not run yet."}
          {data.lastRunProblem ? <span className="ml-1 text-ele-danger">The last run had a problem: {data.lastRunProblem}</span> : null}
        </p>
      )}
    </div>
  );

  let body: ReactNode;
  if (error) {
    body = (
      <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center">
        <p className="text-ele-strong">{error}</p>
        <button type="button" onClick={() => void load()} className={`mt-3 ${SMALL_BTN}`}>
          Retry
        </button>
      </div>
    );
  } else if (!items) {
    body = (
      <Loader label="Loading the chases..." rows={5} />
    );
  } else if (items.length === 0) {
    body = (
      <EmptyState
        message={
          view === "due"
            ? "No chases are due today."
            : view === "calls"
              ? "No chase is waiting on a phone call."
              : view === "referral"
                ? "No debt is ready to refer for recovery."
                : view === "ended"
                  ? "No ended chases."
                  : "Nothing is being chased."
        }
        hint="Chases are found each morning from quotes, estimates, renewals, purchase orders, LOLER reports, invoices and the questions our emails asked."
      />
    );
  } else {
    body = (
      <>
        {view !== "ended" && (
          <div className="mb-3 flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <label className="flex min-h-11 items-center gap-2 text-sm text-ele-strong sm:min-h-0">
              <input
                type="checkbox"
                checked={allTicked}
                disabled={selectable.length === 0}
                onChange={() => setSelected(allTicked ? new Set() : new Set(selectable.map((c) => c.id)))}
                className="h-5 w-5 accent-ele-green"
              />
              Select all drafts that can be sent
            </label>
            <Button size="sm" className="max-sm:w-full" disabled={chosen.length === 0} onClick={() => setConfirmFor(chosen)}>
              Send selected ({chosen.length})
            </Button>
          </div>
        )}
        {data && data.total > items.length && (
          <p className="mb-2 text-xs text-ele-body">
            Showing {items.length} of {data.total}. Search to narrow the list.
          </p>
        )}
        <ul className="space-y-2" aria-label="Chases">
          {items.map((c) => (
            <ChaseRow
              key={c.id}
              chase={c}
              ticked={selected.has(c.id)}
              expanded={expanded.has(c.id)}
              onToggle={() => toggle(c.id)}
              onExpand={() =>
                setExpanded((prev) => {
                  const n = new Set(prev);
                  if (n.has(c.id)) n.delete(c.id);
                  else n.add(c.id);
                  return n;
                })
              }
              onChanged={replaceRow}
              onReload={() => void load()}
              onSend={() => setConfirmFor([c])}
              onRefer={() => setReferFor(c)}
            />
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="min-w-0" data-testid="chases-tab">
      {header}
      <div className="mb-3 flex min-w-0 flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        <div data-hscroll role="tablist" aria-label="Which chases" className="-mx-1 flex min-w-0 gap-2 overflow-x-auto px-1 pb-1">
          {CHASE_VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={view === v}
              onClick={() => setView(v)}
              data-testid={`chases-view-${v}`}
              className={`shrink-0 whitespace-nowrap rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide min-h-11 sm:min-h-0 ${
                view === v ? "border-ele-green text-ele-green" : "border-ele-purple-accent text-ele-body hover:text-ele-strong"
              }`}
            >
              {VIEW_LABELS[v]}
              {v === "due" && m ? ` (${m.dueNow})` : v === "calls" && m ? ` (${m.calls})` : ""}
            </button>
          ))}
        </div>
        <form
          className="flex min-w-0 flex-col gap-2 sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            void load();
          }}
        >
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title, reference, name, email"
            aria-label="Search the chases"
            className={`${INPUT} min-w-0 sm:w-64`}
          />
          <button type="submit" className={SMALL_BTN} disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </button>
          <button type="button" className={SMALL_BTN} onClick={() => setShowNew((v) => !v)} aria-expanded={showNew}>
            Chase something by hand
          </button>
          <ConfirmButton
            onConfirm={() => void runNow()}
            label="Check now"
            confirmLabel="Press again: check and draft"
            busy={running}
            busyLabel="Checking..."
            className={SMALL_BTN}
            armedClassName="!text-ele-green font-semibold"
          />
        </form>
      </div>
      {runNote && (
        <p role="status" className="mb-3 text-sm text-ele-body">
          {runNote}
        </p>
      )}
      {showNew && (
        <NewChaseForm
          onDone={async () => {
            setShowNew(false);
            await load();
          }}
          onCancel={() => setShowNew(false)}
        />
      )}
      {results && <SendResults results={results} onClose={() => setResults(null)} />}
      {body}
      {confirmFor && (
        <ChaseSendConfirm
          targets={confirmFor}
          onCancel={() => setConfirmFor(null)}
          onSent={async (r) => {
            const labels = new Map(confirmFor.map((t) => [t.id, chaseLabel(t)]));
            setResults(r.map((x) => ({ result: x, label: labels.get(x.id) ?? "A chase" })));
            setConfirmFor(null);
            setSelected(new Set());
            await load();
          }}
          onUncertain={() => void load()}
        />
      )}
      {referFor && (
        <ReferConfirm
          chase={referFor}
          onClose={async (changed) => {
            setReferFor(null);
            if (changed) await load();
          }}
        />
      )}
    </div>
  );
}

function FailureList({ failures }: { failures: ChaseFailure[] }) {
  return (
    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
      {failures.map((f, i) => (
        <li key={i} className="[overflow-wrap:anywhere]">
          {f.detail}
          {f.line ? <span className="block opacity-80">"{f.line}"</span> : null}
        </li>
      ))}
    </ul>
  );
}

function ChaseRow({
  chase: c,
  ticked,
  expanded,
  onToggle,
  onExpand,
  onChanged,
  onReload,
  onSend,
  onRefer,
}: {
  chase: Chase;
  ticked: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  onChanged: (c: Chase | null | undefined) => void;
  onReload: () => void;
  onSend: () => void;
  onRefer: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [panel, setPanel] = useState<null | "snooze" | "stop" | "change" | "redraft">(null);
  const [snoozeTo, setSnoozeTo] = useState("");
  const [stopReason, setStopReason] = useState("");
  const [tone, setTone] = useState<"" | ChaseTone | "auto">("");
  const [dueAt, setDueAt] = useState(isoDay(c.dueOverrideAt));
  const [instruction, setInstruction] = useState("");
  const [editText, setEditText] = useState<string | null>(null);
  const [editWarnings, setEditWarnings] = useState<ChaseFailure[] | null>(null);
  const open = c.status === "open";
  const selectable = canSelectChase(c);
  const label = chaseLabel(c);
  const isDebt = c.owedKind === "payment" && c.sourceKind === "invoice";

  async function act(key: string, fn: () => Promise<Chase | null | undefined | void>, fallback: string, done?: string) {
    setBusy(key);
    setRowError(null);
    setNote(null);
    try {
      const next = await fn();
      if (next) onChanged(next);
      if (done) setNote(done);
      setPanel(null);
    } catch (err) {
      setRowError(friendlyApiError(err, fallback));
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className={`min-w-0 rounded-ele border bg-ele-surface px-3 py-3 ${ticked ? "border-ele-green" : "border-ele-purple-accent"}`} data-testid={`chase-row-${c.id}`} aria-label={label}>
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex w-11 shrink-0 justify-center sm:w-6">
          {selectable ? (
            <label className="flex min-h-11 min-w-11 items-center justify-center sm:min-h-0 sm:min-w-0">
              <input type="checkbox" checked={ticked} onChange={onToggle} aria-label={`Select ${label}`} className="h-5 w-5 accent-ele-green" />
            </label>
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="shrink-0" data-testid="chase-tone">
              <StatusPill domain="chaseTone" status={c.tone} />
            </span>
            <span className="max-w-full rounded-ele border border-ele-hairline px-2 py-0.5 font-menu text-xs tracking-wide text-ele-body">{c.ladderLabel}</span>
            {c.mode === "call" && <StatusPill word="Phone them" tone="amber" className="shrink-0" />}
            {c.snoozedUntil && <span className="shrink-0 text-xs text-ele-body">Snoozed until {formatDateLong(c.snoozedUntil)}</span>}
            {open && c.heldReason && <StatusPill word="Held, check with the office" tone="amber" className="shrink-0" />}
            {open && c.heldReason && (
              <span className="text-xs text-ele-body [overflow-wrap:anywhere]" data-testid="chase-held-reason">
                {c.heldReason}
              </span>
            )}
            {!open && <span className="shrink-0 text-xs text-ele-body">Ended {formatDateLong(c.endedAt)}{c.endedByName ? ` by ${c.endedByName}` : ""}</span>}
          </div>
          <p className="mt-1 font-heading text-ele-strong [overflow-wrap:anywhere]">
            {c.link ? (
              <Link to={c.link} className="hover:underline">
                {label}
              </Link>
            ) : (
              label
            )}
          </p>
          <p className="mt-0.5 text-xs text-ele-body [overflow-wrap:anywhere]">
            {c.owedLabel} from {c.counterpartyLabel.toLowerCase()}
            {c.contactName ? ` ${c.contactName}` : ""}
            {c.contactEmail ? <span className="text-ele-strong"> {c.contactEmail}</span> : null}
            {c.contactPhone ? ` · ${c.contactPhone}` : ""}
          </p>
          <p className="mt-0.5 text-xs text-ele-body">
            Waiting since {c.sinceLabel} ({c.owedWorkingDays} working {c.owedWorkingDays === 1 ? "day" : "days"}) · {c.step} sent so far
            {c.deadlineAt ? ` · needed by ${formatDateLong(c.deadlineAt)}${c.deadlineLabel ? ` (${c.deadlineLabel})` : ""}` : ""}
          </p>
          {open && c.dueReason && (
            <p className="mt-1 text-sm text-ele-strong" data-testid={`chase-due-${c.id}`}>
              {c.dueReason}
            </p>
          )}
          {open && c.toneReason && (
            <p className="mt-0.5 text-xs text-ele-body" data-testid={`chase-why-${c.id}`}>
              Why {c.tone === "hard" ? "firm" : "soft"}: {c.toneReason}
            </p>
          )}
          {(c.toneOverride || c.dueOverrideAt) && (
            <p className="mt-0.5 text-xs text-ele-body">
              Set by {c.overrideByName ?? "the office"}: {c.toneOverride ? `${c.toneOverride === "hard" ? "firm" : "soft"} tone` : ""}
              {c.toneOverride && c.dueOverrideAt ? ", " : ""}
              {c.dueOverrideAt ? `due ${formatDateLong(c.dueOverrideAt)}` : ""}. The daily run keeps it.
            </p>
          )}
          {c.promiseText && (
            <p className="mt-0.5 text-xs text-ele-body [overflow-wrap:anywhere]">
              They said: "{c.promiseText}"{c.promiseDate ? ` (by ${formatDateLong(c.promiseDate)})` : ""}
            </p>
          )}
          {c.finalNoticeSentAt && (
            <p className="mt-0.5 text-xs text-ele-body">
              Final notice sent {formatDateLong(c.finalNoticeSentAt)}, pay by {formatDateLong(c.finalNoticePayBy)}.
              {c.referralSentAt ? ` Referred for recovery ${formatDateLong(c.referralSentAt)}.` : ""}
            </p>
          )}
          {c.stoppedReason && <p className="mt-0.5 text-xs text-ele-body">Why it ended: {c.stoppedReason}</p>}
          {c.draft?.failures && c.draft.failures.length > 0 && (
            <div className={`mt-2 ${AMBER_NOTE}`} data-testid={`chase-failures-${c.id}`}>
              <p>The draft for this step failed a check, so nothing was put in Gmail. Write it yourself, or redraft with an instruction:</p>
              <FailureList failures={c.draft.failures} />
            </div>
          )}
          {c.draft && c.draft.body && (
            <p className="mt-1 text-xs text-ele-body">
              Draft for step {c.draft.step}: <span className="text-ele-strong">{c.draft.label ?? "Draft"}</span>
              {c.draft.miss ? ` (${c.draft.miss.text})` : ""}
              {c.draft.attachment ? ` · with ${c.draft.attachment}` : ""}
              {c.draft.inGmail ? " · in Gmail" : ""}
            </p>
          )}
          {note && (
            <p role="status" className="mt-2 text-sm text-ele-body">
              {note}
            </p>
          )}
          {rowError && (
            <p role="alert" className={`mt-2 ${DANGER_NOTE}`}>
              {rowError}
            </p>
          )}

          {open && (
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {c.draft?.body && (
                <button type="button" onClick={onExpand} aria-expanded={expanded} className={SMALL_BTN}>
                  {expanded ? "Hide the draft" : "Show the draft"}
                </button>
              )}
              {selectable && (
                <button type="button" onClick={onSend} className={SMALL_BTN}>
                  Send this
                </button>
              )}
              {c.mode === "email" && (
                <button type="button" onClick={() => setPanel(panel === "redraft" ? null : "redraft")} className={SMALL_BTN} disabled={!!busy}>
                  {c.draft ? "Redraft" : "Draft now"}
                </button>
              )}
              <button type="button" onClick={() => setPanel(panel === "change" ? null : "change")} className={SMALL_BTN} disabled={!!busy}>
                Change tone or date
              </button>
              <button type="button" onClick={() => setPanel(panel === "snooze" ? null : "snooze")} className={SMALL_BTN} disabled={!!busy}>
                {c.snoozedUntil ? "Snooze or wake" : "Snooze"}
              </button>
              {c.mode === "email" ? (
                <ConfirmButton
                  onConfirm={() => void act("call", async () => (await api.chases.call(c.id, "convert")).chase, "Could not make this a phone call.", "This chase is now a phone call. Its email draft was removed.")}
                  label="Phone them instead"
                  confirmLabel="Press again: make it a call"
                  busy={busy === "call"}
                  busyLabel="Saving..."
                  className={SMALL_BTN}
                  armedClassName="!text-ele-green font-semibold"
                />
              ) : (
                <ConfirmButton
                  onConfirm={() => void act("call", async () => (await api.chases.call(c.id, "record")).chase, "Could not record the call.", "The call was recorded.")}
                  label="I have called them"
                  confirmLabel="Press again: record the call"
                  busy={busy === "call"}
                  busyLabel="Saving..."
                  className={SMALL_BTN}
                  armedClassName="!text-ele-green font-semibold"
                />
              )}
              {isDebt && (
                <button type="button" onClick={onRefer} className={SMALL_BTN}>
                  {c.referralReady ? "Send the referral" : "Refer for recovery"}
                </button>
              )}
              <button type="button" onClick={() => setPanel(panel === "stop" ? null : "stop")} className={SMALL_BTN} disabled={!!busy}>
                Stop chasing
              </button>
            </div>
          )}
          {!open && (
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <ConfirmButton
                onConfirm={() =>
                  void act(
                    "delete",
                    async () => {
                      await api.chases.remove(c.id);
                      onReload();
                    },
                    "Could not delete this chase.",
                  )
                }
                label="Delete the record"
                confirmLabel="Press again: delete for good"
                busy={busy === "delete"}
                busyLabel="Deleting..."
                className={SMALL_BTN}
              />
            </div>
          )}

          {panel === "redraft" && (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-inset p-3">
              <label className="block text-xs text-ele-body">
                An instruction for the draft (optional, e.g. "mention the visit on Thursday"):
                <input value={instruction} onChange={(e) => setInstruction(e.target.value)} maxLength={600} className={`${INPUT} mt-1 w-full`} />
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  disabled={!!busy}
                  className={SMALL_BTN}
                  onClick={() =>
                    void act(
                      "draft",
                      async () => {
                        const r = await api.chases.draft(c.id, instruction.trim() || undefined);
                        setNote(r.message);
                        if (r.ended) onReload();
                        return r.chase;
                      },
                      "The chase could not be drafted.",
                    )
                  }
                >
                  {busy === "draft" ? "Drafting..." : "Draft it"}
                </button>
                <button type="button" className={SMALL_BTN} onClick={() => setPanel(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {panel === "change" && (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-inset p-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <label className="text-xs text-ele-body">
                  Tone
                  <select value={tone} onChange={(e) => setTone(e.target.value as typeof tone)} className={`${INPUT} mt-1 block w-full`}>
                    <option value="">Leave as it is</option>
                    <option value="soft">Soft</option>
                    <option value="hard">Firm</option>
                    <option value="auto">Let the platform decide</option>
                  </select>
                </label>
                <label className="text-xs text-ele-body">
                  Next chase due
                  <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className={`${INPUT} mt-1 block w-full`} />
                </label>
              </div>
              <p className="mt-1 text-xs text-ele-body">Clear the date to let the platform work it out again.</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  disabled={!!busy}
                  className={SMALL_BTN}
                  onClick={() =>
                    void act(
                      "change",
                      async () => {
                        const change: { tone?: ChaseTone | null; dueAt?: string | null } = {};
                        if (tone === "auto") change.tone = null;
                        else if (tone) change.tone = tone;
                        const was = isoDay(c.dueOverrideAt);
                        if (dueAt !== was) change.dueAt = dueAt || null;
                        if (!("tone" in change) && !("dueAt" in change)) return c;
                        return (await api.chases.override(c.id, change)).chase;
                      },
                      "Could not save the change.",
                      "Saved. The daily run will keep your choice.",
                    )
                  }
                >
                  {busy === "change" ? "Saving..." : "Save"}
                </button>
                <button type="button" className={SMALL_BTN} onClick={() => setPanel(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {panel === "snooze" && (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-inset p-3">
              <label className="text-xs text-ele-body">
                Snooze until
                <input type="date" value={snoozeTo} onChange={(e) => setSnoozeTo(e.target.value)} className={`${INPUT} mt-1 block`} />
              </label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  disabled={!!busy || !snoozeTo}
                  className={SMALL_BTN}
                  onClick={() => void act("snooze", async () => (await api.chases.snooze(c.id, snoozeTo)).chase, "Could not snooze this chase.", `Snoozed until ${formatDateLong(`${snoozeTo}T12:00:00Z`)}.`)}
                >
                  {busy === "snooze" ? "Saving..." : "Snooze"}
                </button>
                {c.snoozedUntil && (
                  <button type="button" disabled={!!busy} className={SMALL_BTN} onClick={() => void act("snooze", async () => (await api.chases.snooze(c.id, null)).chase, "Could not wake this chase.", "Woken.")}>
                    Wake it now
                  </button>
                )}
                <button type="button" className={SMALL_BTN} onClick={() => setPanel(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {panel === "stop" && (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-inset p-3">
              <label className="block text-xs text-ele-body">
                Why stop? (kept on the record)
                <input value={stopReason} onChange={(e) => setStopReason(e.target.value)} maxLength={300} className={`${INPUT} mt-1 w-full`} placeholder="e.g. They rang and it is sorted" />
              </label>
              <p className="mt-1 text-xs text-ele-body">The platform's own unsent draft is removed from Gmail. Nothing is sent.</p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <ConfirmButton
                  onConfirm={() =>
                    void act(
                      "stop",
                      async () => {
                        const r = await api.chases.stop(c.id, stopReason.trim());
                        if (r.draftNotRemoved) setNote(r.draftNotRemoved);
                        onReload();
                        return r.chase;
                      },
                      "Could not stop this chase.",
                    )
                  }
                  label="Stop chasing"
                  confirmLabel="Press again: stop"
                  busy={busy === "stop"}
                  busyLabel="Stopping..."
                  disabled={stopReason.trim().length < 3}
                  className={SMALL_BTN}
                />
                <button type="button" className={SMALL_BTN} onClick={() => setPanel(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {expanded && c.draft?.body && (
            <div className="mt-3 min-w-0 space-y-2" data-testid={`chase-draft-${c.id}`}>
              <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                To <span className="text-ele-strong">{c.draft.to ?? c.contactEmail ?? "no address"}</span>
                {c.draft.cc ? (
                  <>
                    {" "}
                    · Cc <span className="text-ele-strong">{c.draft.cc}</span>
                  </>
                ) : null}{" "}
                · From <span className="text-ele-strong">{c.draft.from ?? "the office mailbox"}</span>
              </p>
              {c.draft.subject && (
                <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                  Subject: <span className="text-ele-strong">{c.draft.subject}</span>
                </p>
              )}
              {editText === null ? (
                <>
                  <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap rounded-ele border border-ele-hairline bg-ele-inset p-3 font-body text-xs text-ele-strong [overflow-wrap:anywhere]">{c.draft.body}</pre>
                  {c.draft.attachment !== "Referral pack and notice of referral" && (
                    <button type="button" className={SMALL_BTN} onClick={() => setEditText(c.draft?.body ?? "")}>
                      Edit the words
                    </button>
                  )}
                </>
              ) : (
                <>
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={10} maxLength={6000} aria-label="The chase's words" className={`${INPUT} w-full font-body text-xs`} />
                  {editWarnings && editWarnings.length > 0 && (
                    <div className={AMBER_NOTE}>
                      <p>Saved, but worth a second look:</p>
                      <FailureList failures={editWarnings} />
                    </div>
                  )}
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      disabled={!!busy || editText.trim().length < 10}
                      className={SMALL_BTN}
                      onClick={() =>
                        void act(
                          "edit",
                          async () => {
                            const r = await api.chases.editDraft(c.id, editText);
                            setEditWarnings(r.warnings);
                            if (r.warnings.length === 0) setEditText(null);
                            return r.chase;
                          },
                          "Could not save the words.",
                          "Your words are saved and in the Gmail draft.",
                        )
                      }
                    >
                      {busy === "edit" ? "Saving..." : "Save the words"}
                    </button>
                    <button
                      type="button"
                      className={SMALL_BTN}
                      onClick={() => {
                        setEditText(null);
                        setEditWarnings(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {c.sends.length > 0 && (
            <details className="mt-2 text-xs text-ele-body">
              <summary className="cursor-pointer">Sent so far ({c.sends.length})</summary>
              <ul className="mt-1 space-y-0.5">
                {c.sends.map((s, i) => (
                  <li key={i}>
                    Step {s.step}, {s.tone === "hard" ? "firm" : "soft"}
                    {s.kind === "final_notice" ? " (final notice)" : s.kind === "referral" ? " (referral)" : ""}, {fmtLondonDateTime(s.at)}
                    {s.via === "gmail" ? ", sent from Gmail" : ""}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      </div>
    </li>
  );
}

type PreviewState = { kind: "loading" } | { kind: "ready"; items: ChaseSendPreviewItem[] } | { kind: "error"; message: string };

/** The branded confirm for a batch: the server's preview first (nothing sent), every To, Cc and From named. */
export function ChaseSendConfirm({
  targets,
  onCancel,
  onSent,
  onUncertain,
}: {
  targets: Chase[];
  onCancel: () => void;
  onSent: (results: ChaseSendResult[]) => void;
  onUncertain: () => void;
}) {
  const [preview, setPreview] = useState<PreviewState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const byId = useMemo(() => new Map(targets.map((t) => [t.id, t])), [targets]);
  const sig = targets.map((t) => `${t.id}:${t.draft?.fingerprint ?? ""}`).join("|");

  useEffect(() => {
    let live = true;
    setPreview({ kind: "loading" });
    api.chases
      .previewSend(targets.filter((t) => t.draft?.fingerprint).map((t) => ({ id: t.id, fingerprint: t.draft!.fingerprint! })))
      .then((res) => live && setPreview({ kind: "ready", items: res.items }))
      .catch((err) => live && setPreview({ kind: "error", message: friendlyApiError(err, "Could not check who these chases go to.") }));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const going = preview.kind === "ready" ? preview.items.filter((p): p is Extract<ChaseSendPreviewItem, { wouldSend: true }> => p.wouldSend) : [];
  const notGoing = preview.kind === "ready" ? preview.items.filter((p): p is Extract<ChaseSendPreviewItem, { wouldSend: false }> => !p.wouldSend) : [];
  const canSend = !busy && preview.kind === "ready" && going.length > 0;

  async function send() {
    const items = going.map((p) => ({ id: p.id, fingerprint: byId.get(p.id)?.draft?.fingerprint ?? null })).filter((i): i is { id: string; fingerprint: string } => !!i.fingerprint);
    if (items.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.chases.send(items);
      onSent(res.results);
    } catch (err) {
      setError(`${friendlyApiError(err, "The send could not be confirmed.")} Some chases may already have gone, so the list has been read again: check it before sending again.`);
      onUncertain();
    } finally {
      setBusy(false);
    }
  }

  const title = targets.length === 1 ? "Send this chase?" : `Send ${targets.length} chases?`;
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onCancel()} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">{title}</h2>
      <p className="mt-2 text-sm text-ele-body">Each goes as a reply in its own conversation. An email cannot be recalled once sent.</p>
      {preview.kind === "loading" && (
        <p role="status" className="mt-3 text-sm text-ele-body">
          Checking each chase...
        </p>
      )}
      {preview.kind === "error" && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {preview.message}
        </p>
      )}
      {preview.kind === "ready" && (
        <>
          {going.length > 0 && (
            <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto" aria-label="Who each chase goes to">
              {going.map((p) => (
                <li key={p.id} className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-sm">
                  <p className="truncate text-ele-strong">{p.title}</p>
                  <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                    To <span className="font-semibold text-ele-strong">{p.to}</span>
                    {p.cc ? (
                      <>
                        {" "}
                        · Cc <span className="font-semibold text-ele-strong">{p.cc}</span>
                      </>
                    ) : null}
                  </p>
                  <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                    From <span className="text-ele-strong">{p.from ?? "the office mailbox"}</span>
                    {p.fromReason ? ` (${p.fromReason})` : ""}
                  </p>
                  <p className="text-xs text-ele-body [overflow-wrap:anywhere]">
                    Subject: <span className="text-ele-strong">{p.subject}</span>
                  </p>
                  {p.attachment ? <p className={`mt-1 ${AMBER_NOTE}`}>With {p.attachment}</p> : null}
                </li>
              ))}
            </ul>
          )}
          {notGoing.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-ele-strong">{notGoing.length === 1 ? "This one will not be sent:" : `These ${notGoing.length} will not be sent:`}</p>
              <ul className="mt-1 space-y-2" aria-label="Chases that will not be sent">
                {notGoing.map((p) => (
                  <li key={p.id} className={AMBER_NOTE}>
                    <p className="truncate">{p.title ?? byId.get(p.id)?.title ?? p.id}</p>
                    <p className="text-xs">{p.message}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {going.length === 0 && <p className="mt-3 text-sm text-ele-body">Nothing can be sent as it stands. Close this and look at the list again.</p>}
        </>
      )}
      {error && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onCancel} disabled={busy} className={MODAL_SECONDARY_BTN}>
          Cancel
        </button>
        <button type="button" onClick={() => void send()} disabled={!canSend} className={MODAL_PRIMARY_BTN}>
          {busy ? "Sending..." : preview.kind === "loading" ? "Checking..." : going.length <= 1 ? "Send 1 chase" : `Send ${going.length} chases`}
        </button>
      </div>
    </ModalShell>
  );
}

/**
 * The referral pair: prepared as two Gmail drafts, then sent together (never one alone). The confirm names both recipients
 * from the server's own preview, which refuses a non-contract debt until the final notice was SENT and its pay-by passed.
 */
export function ReferConfirm({ chase, onClose }: { chase: Chase; onClose: (changed: boolean) => void }) {
  const [state, setState] = useState<{ kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; res: Awaited<ReturnType<typeof api.chases.refer>> }>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.chases
      .refer(chase.id, { preview: true })
      .then((res) => live && setState({ kind: "ready", res }))
      .catch((err) => live && setState({ kind: "error", message: friendlyApiError(err, "The referral cannot be prepared yet.") }));
    return () => {
      live = false;
    };
  }, [chase.id]);

  const res = state.kind === "ready" ? state.res : null;
  const prepared = !!res?.prepared;

  async function go() {
    if (!res) return;
    setBusy(true);
    setError(null);
    try {
      if (!prepared) {
        const r = await api.chases.refer(chase.id, { preview: false });
        setDone(r.message ?? "Both are drafted in Gmail. Open this again to send them together.");
      } else {
        const r = await api.chases.refer(chase.id, { preview: false, fingerprint: res.fingerprint ?? undefined });
        setDone(`Sent: the referral pack to ${r.packTo} and the notice of referral to ${r.noticeTo}.${r.note ? ` ${r.note}` : ""}`);
      }
    } catch (err) {
      setError(friendlyApiError(err, prepared ? "The referral was not sent." : "The referral could not be prepared."));
    } finally {
      setBusy(false);
    }
  }

  const title = prepared ? "Send the referral?" : "Prepare the referral?";
  return (
    <ModalShell ariaLabel={title} onClose={() => !busy && onClose(!!done)} closeOnBackdrop width="max-w-lg" panelClassName="p-5 shadow-xl">
      <h2 className="font-heading text-lg text-ele-strong">{title}</h2>
      <p className="mt-2 text-sm text-ele-body">{chaseLabel(chase)}</p>
      {state.kind === "loading" && (
        <p role="status" className="mt-3 text-sm text-ele-body">
          Checking...
        </p>
      )}
      {state.kind === "error" && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {state.message}
        </p>
      )}
      {res && !prepared && <p className="mt-3 text-sm text-ele-body">{res.message}</p>}
      {res && prepared && res.wouldSend && (
        <div className="mt-3 space-y-2 text-sm">
          <p className="text-ele-body">Both go together, or neither does:</p>
          <p className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-xs [overflow-wrap:anywhere]">
            The referral pack, with the final notice letter, to <span className="font-semibold text-ele-strong">{res.pack?.to}</span>
          </p>
          <p className="rounded-ele border border-ele-hairline bg-ele-inset px-3 py-2 text-xs [overflow-wrap:anywhere]">
            The notice of referral to <span className="font-semibold text-ele-strong">{res.notice?.to}</span>
            {res.notice?.cc ? ` · Cc ${res.notice.cc}` : ""}
          </p>
        </div>
      )}
      {res && prepared && !res.wouldSend && (
        <p className={`mt-3 ${AMBER_NOTE}`}>{res.message ?? "The referral cannot be sent as it stands."}</p>
      )}
      {done && (
        <p role="status" className="mt-3 text-sm text-ele-green">
          {done}
        </p>
      )}
      {error && (
        <p role="alert" className={`mt-3 ${DANGER_NOTE}`}>
          {error}
        </p>
      )}
      <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={() => onClose(!!done)} disabled={busy} className={MODAL_SECONDARY_BTN}>
          {done ? "Close" : "Cancel"}
        </button>
        {!done && (
          <button type="button" onClick={() => void go()} disabled={busy || !res || (prepared && !res.wouldSend)} className={MODAL_PRIMARY_BTN}>
            {busy ? "Working..." : prepared ? "Send both" : "Draft both"}
          </button>
        )}
      </div>
    </ModalShell>
  );
}

const OWED_OPTIONS: Array<{ value: "reply" | "decision" | "information" | "document" | "delivery"; label: string }> = [
  { value: "reply", label: "A reply" },
  { value: "decision", label: "A decision" },
  { value: "information", label: "Information or access" },
  { value: "document", label: "A document" },
  { value: "delivery", label: "A delivery" },
];

/** Detector (d): open a chase by hand, for something no record or email would find. */
function NewChaseForm({ onDone, onCancel }: { onDone: () => void | Promise<void>; onCancel: () => void }) {
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [owed, setOwed] = useState<(typeof OWED_OPTIONS)[number]["value"]>("reply");
  const [dueAt, setDueAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = title.trim().length >= 3 && /.+@.+\..+/.test(email.trim());

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api.chases.create(
        { kind: "manual", title: title.trim(), contactEmail: email.trim(), ...(name.trim() ? { contactName: name.trim() } : {}), owedKind: owed },
        { ...(dueAt ? { dueAt } : {}), preview: false },
      );
      await onDone();
    } catch (err) {
      setError(friendlyApiError(err, "The chase could not be opened."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4" data-testid="chase-new">
      <p className="text-sm text-ele-strong">Chase something by hand</p>
      <p className="mt-1 text-xs text-ele-body">It follows the general ladder and is drafted each morning it is due. Nothing is sent until you press Send.</p>
      <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-2">
        <label className="text-xs text-ele-body">
          What are we waiting for?
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={`${INPUT} mt-1 w-full`} placeholder="e.g. Access dates for the motor room" />
        </label>
        <label className="text-xs text-ele-body">
          Kind
          <select value={owed} onChange={(e) => setOwed(e.target.value as typeof owed)} className={`${INPUT} mt-1 w-full`}>
            {OWED_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-ele-body">
          Their email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} className={`${INPUT} mt-1 w-full`} />
        </label>
        <label className="text-xs text-ele-body">
          Their name (optional)
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} className={`${INPUT} mt-1 w-full`} />
        </label>
        <label className="text-xs text-ele-body">
          First chase due (optional)
          <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)} className={`${INPUT} mt-1 w-full`} />
        </label>
      </div>
      {error && (
        <p role="alert" className={`mt-2 ${DANGER_NOTE}`}>
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <Button size="sm" className="max-sm:w-full" disabled={!ok || busy} onClick={() => void save()}>
          {busy ? "Opening..." : "Open the chase"}
        </Button>
        <button type="button" onClick={onCancel} className={SMALL_BTN}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function SendResults({ results, onClose }: { results: Array<{ result: ChaseSendResult; label: string }>; onClose: () => void }) {
  const sent = results.filter((r) => r.result.outcome === "sent").length;
  return (
    <div className="mb-4 rounded-ele border border-ele-hairline bg-ele-surface px-4 py-3" role="status" data-testid="chases-results">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-ele-strong">
          {sent} of {results.length} sent.
        </p>
        <button type="button" onClick={onClose} className={SMALL_BTN}>
          Close
        </button>
      </div>
      <ul className="mt-2 space-y-1 text-sm" aria-label="What happened to each chase">
        {results.map(({ result: r, label }) => (
          <li key={r.id} className="min-w-0 [overflow-wrap:anywhere]">
            <span className="text-ele-strong">{label}:</span>{" "}
            {r.outcome === "sent" && <span className="text-ele-green">sent to {r.to}</span>}
            {r.outcome === "sent" && r.note ? <span className="block text-[var(--ele-amber-text-soft)]">{r.note}</span> : null}
            {r.outcome === "failed" && <span className="text-ele-danger">failed: {r.message}</span>}
            {r.outcome === "refused" && <span className="text-[var(--ele-amber-text-soft)]">not sent: {r.message}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
